import React from 'react';

type GhostProps = {
  className?: string;
};

/**
 * Decorative chart silhouettes for the pre-assessment dashboard preview.
 * Shapes mirror the live gauge, gap bars, and radar modules.
 */
export function ClientDashboardGhostGauge({ className }: GhostProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 200 120"
      role="img"
      aria-hidden
      focusable="false"
    >
      <path
        d="M28 98 A72 72 0 0 1 172 98"
        fill="none"
        stroke="rgba(15, 107, 102, 0.12)"
        strokeWidth="14"
        strokeLinecap="round"
      />
      <path
        d="M28 98 A72 72 0 0 1 118 38"
        fill="none"
        stroke="rgba(15, 107, 102, 0.38)"
        strokeWidth="14"
        strokeLinecap="round"
      />
      <text
        x="100"
        y="78"
        textAnchor="middle"
        fontSize="22"
        fontWeight="600"
        fill="rgba(32, 48, 46, 0.22)"
      >
        —
      </text>
      <text
        x="100"
        y="96"
        textAnchor="middle"
        fontSize="10"
        fontWeight="500"
        fill="rgba(107, 120, 118, 0.55)"
      >
        of target
      </text>
    </svg>
  );
}

export function ClientDashboardGhostRadar({ className }: GhostProps) {
  const cx = 100;
  const cy = 100;
  const r = 72;
  const spokes = 5;
  const points = Array.from({ length: spokes }, (_, i) => {
    const angle = (Math.PI * 2 * i) / spokes - Math.PI / 2;
    const radius = r * (0.45 + (i % 3) * 0.12);
    return {
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
    };
  });
  const polygon = points.map((p) => `${p.x},${p.y}`).join(' ');

  return (
    <svg
      className={className}
      viewBox="0 0 200 200"
      role="img"
      aria-hidden
      focusable="false"
    >
      {[0.25, 0.5, 0.75, 1].map((scale) => (
        <polygon
          key={scale}
          points={Array.from({ length: spokes }, (_, i) => {
            const angle = (Math.PI * 2 * i) / spokes - Math.PI / 2;
            return `${cx + Math.cos(angle) * r * scale},${cy + Math.sin(angle) * r * scale}`;
          }).join(' ')}
          fill="none"
          stroke="rgba(32, 48, 46, 0.08)"
          strokeWidth="1"
        />
      ))}
      {Array.from({ length: spokes }, (_, i) => {
        const angle = (Math.PI * 2 * i) / spokes - Math.PI / 2;
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={cx + Math.cos(angle) * r}
            y2={cy + Math.sin(angle) * r}
            stroke="rgba(32, 48, 46, 0.08)"
            strokeWidth="1"
          />
        );
      })}
      <polygon
        points={polygon}
        fill="rgba(15, 107, 102, 0.12)"
        stroke="rgba(15, 107, 102, 0.35)"
        strokeWidth="2"
      />
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r="3.5"
          fill="rgba(15, 107, 102, 0.45)"
        />
      ))}
    </svg>
  );
}

export function ClientDashboardGhostGapBars({ className }: GhostProps) {
  const rows = [
    { label: 0.72, current: 0.38, gap: 0.22 },
    { label: 0.55, current: 0.52, gap: 0.18 },
    { label: 0.4, current: 0.28, gap: 0.35 },
  ];

  return (
    <svg
      className={className}
      viewBox="0 0 280 108"
      role="img"
      aria-hidden
      focusable="false"
    >
      {rows.map((row, index) => {
        const y = 12 + index * 34;
        const trackW = 248;
        const x = 16;
        const currentW = trackW * row.current;
        const gapW = trackW * row.gap;
        return (
          <g key={index}>
            <rect
              x={x}
              y={y + 14}
              width={trackW}
              height="8"
              rx="4"
              fill="rgba(15, 107, 102, 0.08)"
            />
            <rect
              x={x}
              y={y + 14}
              width={currentW}
              height="8"
              rx="4"
              fill="rgba(15, 107, 102, 0.35)"
            />
            <rect
              x={x + currentW}
              y={y + 14}
              width={gapW}
              height="8"
              fill="url(#client-dash-ghost-gap)"
              opacity="0.85"
            />
            <rect x={x} y={y} width={72} height="6" rx="2" fill="rgba(32, 48, 46, 0.1)" />
          </g>
        );
      })}
      <defs>
        <pattern
          id="client-dash-ghost-gap"
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width="3" height="6" fill="rgba(254, 122, 89, 0.45)" />
          <rect x="3" width="3" height="6" fill="rgba(254, 122, 89, 0.2)" />
        </pattern>
      </defs>
    </svg>
  );
}

export function ClientDashboardGhostWordCloud({ className }: GhostProps) {
  const blobs = [
    { x: 48, y: 52, w: 56, h: 22, r: 11 },
    { x: 118, y: 38, w: 72, h: 26, r: 13 },
    { x: 88, y: 78, w: 44, h: 18, r: 9 },
    { x: 148, y: 72, w: 52, h: 20, r: 10 },
    { x: 28, y: 88, w: 38, h: 16, r: 8 },
  ];

  return (
    <svg
      className={className}
      viewBox="0 0 220 120"
      role="img"
      aria-hidden
      focusable="false"
    >
      {blobs.map((b, i) => (
        <rect
          key={i}
          x={b.x}
          y={b.y}
          width={b.w}
          height={b.h}
          rx={b.r}
          fill={`rgba(15, 107, 102, ${0.1 + (i % 3) * 0.06})`}
        />
      ))}
    </svg>
  );
}
